import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useCloneTemplate, useTemplates, type Template } from '../../api/templates.api';

/* ── Screen — Select a starting point ──
   Opens from the gallery's "New template" button. Two tabs (Layouts /
   Templates) over the same card grid. Picking a card clones the source
   into the caller's org and navigates to the builder. */

type Tab = 'layouts' | 'templates';
type ThumbMode = 'skeleton' | 'preview';

export interface LayoutPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional override for the navigate target (used by tests). */
  onPicked?: (newTemplate: Template) => void;
}

export function LayoutPickerModal({ open, onClose, onPicked }: LayoutPickerModalProps) {
  const [tab, setTab] = useState<Tab>('layouts');
  const navigate = useNavigate();
  const clone = useCloneTemplate();
  const { data: layouts } = useTemplates({ kind: 'LAYOUT_STRUCTURE' });
  const { data: templates } = useTemplates({ kind: 'CONTENT_TEMPLATE' });

  const visible: Template[] = useMemo(() => {
    const rows = tab === 'layouts' ? layouts?.data ?? [] : templates?.data ?? [];
    // System templates first, then own. The Layout picker is primarily about
    // system content — recruiters' own templates are picked from the gallery.
    return [...rows].sort((a, b) => Number(b.is_system) - Number(a.is_system));
  }, [tab, layouts, templates]);

  if (!open) return null;

  const handlePick = async (t: Template) => {
    try {
      const cloned = await clone.mutateAsync({ id: t.id });
      onClose();
      if (onPicked) onPicked(cloned);
      else navigate(`/templates/${cloned.id}/edit`);
    } catch {
      // Errors are surfaced by useToast in caller paths; the modal stays
      // open if the clone failed so the user can retry.
    }
  };

  return createPortal(
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--modal-overlay-bg, rgba(0,0,0,0.4))',
          zIndex: 100,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="layout-picker-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 101,
          width: '100%',
          maxWidth: '1100px',
          maxHeight: '90vh',
          backgroundColor: '#fff',
          borderRadius: 12,
          boxShadow: '0 24px 64px rgba(30,27,75,.18)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            padding: '20px 28px 14px',
            borderBottom: '1px solid var(--color-border-default, #ececf2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div>
            <h2
              id="layout-picker-title"
              style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--color-text-brand, #1e1b4b)' }}
            >
              Select a starting point
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-muted, #6b6b7d)' }}>
              Pick a column structure or a fully styled template. You can customise everything afterwards.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 22,
              cursor: 'pointer',
              color: 'var(--color-text-muted, #6b6b7d)',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </header>

        <nav role="tablist" style={{ display: 'flex', gap: 0, padding: '0 28px', borderBottom: '1px solid var(--color-border-default, #ececf2)' }}>
          <TabBtn active={tab === 'layouts'} onClick={() => setTab('layouts')}>
            Layouts {layouts ? `(${layouts.data.filter((t) => t.kind === 'LAYOUT_STRUCTURE').length})` : ''}
          </TabBtn>
          <TabBtn active={tab === 'templates'} onClick={() => setTab('templates')}>
            Templates {templates ? `(${templates.data.filter((t) => t.kind === 'CONTENT_TEMPLATE').length})` : ''}
          </TabBtn>
        </nav>

        <div style={{ overflow: 'auto', padding: '20px 28px 28px' }}>
          {visible.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted, #6b6b7d)' }}>No items yet: run the seed to populate.</p>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 20,
              }}
            >
              {visible.map((t) => (
                <PickerCard
                  key={t.id}
                  template={t}
                  onPick={() => handlePick(t)}
                  busy={clone.isPending}
                  showToggle={tab === 'layouts'}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '12px 18px',
        background: 'transparent',
        border: 'none',
        borderBottom: `2px solid ${active ? 'var(--color-action-primary, #1e1b4b)' : 'transparent'}`,
        marginBottom: -1,
        color: active ? 'var(--color-text-brand, #1e1b4b)' : 'var(--color-text-muted, #6b6b7d)',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: active ? 700 : 500,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function PickerCard({
  template,
  onPick,
  busy,
  showToggle,
}: {
  template: Template;
  onPick: () => void;
  busy: boolean;
  showToggle: boolean;
}) {
  const [mode, setMode] = useState<ThumbMode>(showToggle ? 'skeleton' : 'preview');
  return (
    <article
      style={{
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#fff',
        transition: 'box-shadow .15s, transform .15s',
        display: 'flex',
        flexDirection: 'column',
      }}
      data-testid={`picker-card-${template.id}`}
    >
      <div
        style={{
          position: 'relative',
          height: 240,
          overflow: 'hidden',
          borderBottom: '1px solid var(--color-border-default, #ececf2)',
          backgroundColor: 'var(--color-surface-muted, #f4f5f9)',
        }}
      >
        {mode === 'preview' ? (
          <iframe
            title={`${template.name} preview`}
            srcDoc={template.body_html}
            // No scripts, forms, or navigation — `{{calendar_url}}` etc.
            // would otherwise navigate the iframe back into the Engage app.
            sandbox=""
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 600,
              height: 800,
              border: 0,
              transform: 'scale(0.45)',
              transformOrigin: 'top left',
              pointerEvents: 'none',
            }}
          />
        ) : (
          <SkeletonThumb template={template} />
        )}
      </div>
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--color-text-brand, #1e1b4b)' }}>
            {template.name}
          </h3>
          {template.tier && <Pill>{template.tier.toLowerCase()}</Pill>}
        </header>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted, #6b6b7d)' }}>
          {template.category ? template.category.replace(/_/g, ' ').toLowerCase() : 'general'}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {showToggle && (
            <div role="group" aria-label="Thumb mode" style={{ display: 'inline-flex', border: '1px solid var(--color-border-default, #ececf2)', borderRadius: 6, overflow: 'hidden' }}>
              <ToggleBtn active={mode === 'skeleton'} onClick={() => setMode('skeleton')}>Layout</ToggleBtn>
              <ToggleBtn active={mode === 'preview'} onClick={() => setMode('preview')}>Preview</ToggleBtn>
            </div>
          )}
          <button
            type="button"
            onClick={onPick}
            disabled={busy}
            data-testid={`pick-${template.id}`}
            style={{
              marginLeft: 'auto',
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: 'var(--color-action-primary, #1e1b4b)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? '…' : 'Use this'}
          </button>
        </div>
      </div>
    </article>
  );
}

function ToggleBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 10px',
        background: active ? 'var(--color-action-primary, #1e1b4b)' : '#fff',
        color: active ? '#fff' : 'var(--color-text-muted, #6b6b7d)',
        border: 'none',
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 999,
        background: 'var(--color-blue-50, #e8edff)',
        color: 'var(--color-brand-blue, #3b5bdb)',
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.04,
      }}
    >
      {children}
    </span>
  );
}

/** Skeleton placeholder — one light-grey rectangle per block in the tree.
 *  Multi-column rows render columns side-by-side. Deliberately simple —
 *  the goal is "give a rough sense of the layout" not "render the
 *  template". */
function SkeletonThumb({ template }: { template: Template }) {
  const rows = template.blocks?.rows ?? [];
  return (
    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6, height: '100%', overflow: 'hidden' }}>
      {rows.map((r) => {
        const colCount = r.layout === '1-2' ? 2 : r.layout === '1-3' ? 3 : 1;
        const cols = r.columns.slice(0, colCount);
        return (
          <div key={r.id} style={{ display: 'flex', gap: 4 }}>
            {cols.map((c) => (
              <div key={c.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {c.blocks.map((b) => (
                  <div
                    key={b.id}
                    style={{
                      background: blockColor(b.type),
                      height: blockHeight(b.type),
                      borderRadius: 3,
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function blockColor(type: string): string {
  if (type === 'heading') return '#bfc2d1';
  if (type === 'image') return '#c9ccd8';
  if (type === 'button') return '#aeb2c4';
  if (type === 'footer') return '#e3e4ec';
  return '#dfe2ec';
}

function blockHeight(type: string): number {
  if (type === 'heading') return 14;
  if (type === 'image') return 36;
  if (type === 'button') return 18;
  if (type === 'spacer') return 6;
  if (type === 'footer') return 12;
  return 10;
}
